import client from './client';

// ── Legacy DynamoDB Parcel reads (still used by ParcelPicker / member forms) //
// The DynamoDB-backed /parcels/{id} and /parcels/search endpoints remain in
// place for ParcelPicker.js (member form parcel lookup). They'll return 404
// since no path now writes to dev-wildfire-parcels — that path is a separate
// follow-up cleanup. Kept exported so existing imports compile.

export const getParcel = (id) =>
  client.get(`/parcels/${id}`).then((r) => r.data);

export const searchParcels = (ref) =>
  client.get(`/parcels/search?ref=${encodeURIComponent(ref)}`).then((r) => r.data);


// ── Parcel reads (S3 Parquet pipeline) ─────────────────────────────────── //

// Reads parcels for the tenant from the Parquet pipeline (DuckDB-backed
// /parcels/by-tenant/{id}). `params` accepts `bbox`, `boundary_name`,
// `limit`, and `include_geometry`. Response shape: { tenant_id, parcels:
// [{LISKEY, ParcelNo, geometry, …}], total, truncated }. Each parcel's
// `geometry` is GeoJSON (server converts WKB → GeoJSON via DuckDB
// ST_AsGeoJSON) so it drops straight into Leaflet.
export const listTenantParcels = async (tenantId, params, config) => {
  const { data } = await client.get(`/parcels/by-tenant/${tenantId}`, { params, ...config });
  // Oversize tenants get an S3 offload envelope { url, expires_at, total }.
  // The presigned URL must be fetched WITHOUT our Bearer token, so use raw fetch.
  if (data && typeof data.url === 'string') {
    const resp = await fetch(data.url);
    if (!resp.ok) throw new Error(`Parcel offload fetch failed: ${resp.status}`);
    return await resp.json();
  }
  return data;
};

// Linked-parcel-ref set for a tenant — used to colour parcels on the map
// that have a ParcelOwner row in DynamoDB. `parcel_ref` is treated as the
// LISKEY going forward.
export const listLinkedParcelRefs = (tenantId, config) =>
  client.get(`/parcels/by-tenant/${tenantId}/links`, { ...config }).then((r) => r.data);

// Per-parcel hazard tags for a tenant, with no member identity attached.
// Response: { tenant_id, hazards: [{ parcel_ref, hazard_tags: [...] }], total }.
// Gated on parcel_list (not fpa_member_read), so a view-only role that may see
// which parcels are member parcels — but not who the member is — still gets
// hazard-coloured labels. The server does the member join.
export const listParcelHazards = (tenantId, config) =>
  client.get(`/parcels/by-tenant/${tenantId}/hazards`, { ...config }).then((r) => r.data);

// Per-parcel membership + payment status for a tenant — drives the map's
// parcel colour ramp. Response:
//   { tenant_id, statuses: [{ parcel_ref, status }], total }
// where status is "non_member" | "member_unpaid" | "member_paid". Parcels with
// no ParcelOwner row are absent; the map treats missing as "unlinked".
//
// Like /hazards this names nobody (no member_id, ref or name) and is gated on
// parcel_list alone, so a view-only role gets the full ramp. Supersedes
// /links for colouring — it returns a superset of `linked_refs`.
export const listParcelMemberStatus = (tenantId, config) =>
  client
    .get(`/parcels/by-tenant/${tenantId}/member-status`, { ...config })
    .then((r) => r.data);

// Single parcel (geometry + centroid) by its LISKEY, scoped to a tenant. Used
// by the member-detail map to zoom to and highlight the member's linked parcel.
// LISKEYs may contain '/', so encode the ref segment.
export const getTenantParcelByRef = (tenantId, parcelRef) =>
  client
    .get(`/parcels/by-tenant/${tenantId}/parcel/${encodeURIComponent(parcelRef)}`)
    .then((r) => r.data);

// Cheap cache-version probe. Returns { version, last_reindex_at } — the
// version bumps when a reindex completes. The frontend stamps it onto the
// cached parcel blob and refetches only when it changes.
export const getParcelCacheStatus = () =>
  client.get('/parcels/cache-status').then((r) => r.data);

// Live "if I made this polygon a boundary, how many parcels would it
// contain?" probe used by the FPA Hub's boundary editor. Returns
// `{ count: number }`. The backend never raises on a malformed polygon —
// it just returns `count: 0` — so the editor can call this every time
// the admin closes/edits a shape without worrying about 4xx noise.
export const previewParcelCount = (geometry) =>
  client.post('/parcels/preview-count', { geometry }).then((r) => r.data);


// ── Parcel ↔ Member ownership (keyed on cadastral LISKEY) ─────────────── //
//
// LISKEYs may contain '/', so encodeURIComponent each segment before
// stitching it into the URL. The backend uses :path-style segment match
// to accept those characters cleanly.

export const getParcelMembersByRef = (parcelRef) =>
  client
    .get(`/parcels/by-ref/${encodeURIComponent(parcelRef)}/members`)
    .then((r) => r.data);

export const linkMemberByRef = (parcelRef, memberId) =>
  client
    .post(
      `/parcels/by-ref/${encodeURIComponent(parcelRef)}/members`,
      { member_id: memberId },
    )
    .then((r) => r.data);

export const unlinkMemberByRef = (parcelRef, memberId) =>
  client
    .delete(
      `/parcels/by-ref/${encodeURIComponent(parcelRef)}/members/${memberId}`,
    )
    .then((r) => r.data);
